import { type TextareaProps } from "@/components/podkit/forms/Textarea";
import TextareaWithSuggestions, { type CommandSuggestion } from "@/components/podkit/forms/TextareaWithSuggestions";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { useMembership } from "@/hooks/use-membership";
import { useListCommandPrompts, type PlainPrompt, type PlainAgentExecution } from "@/queries/agent-queries";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { forwardRef, useMemo } from "react";
import { useAgentExecutionActions } from "@/hooks/use-agent-execution-actions";

type Props = {
    agentExecution?: PlainAgentExecution;
    onSuggestionSelect?: (suggestion: CommandSuggestion) => boolean;
    triggerChar?: string;
    popoverSideOffset?: number;
    popoverSide?: "top" | "bottom";
    popoverWidthOffset?: number;
    popoverAlignOffset?: number;
    showBuiltInCommands?: boolean | string[]; // true = all, false = none, array = specific commands
    /** When set, shows this message instead of command suggestions when trigger char is typed */
    disabledMessage?: string;
} & TextareaProps &
    PropsWithClassName;

const ChatInputWithPrompts = forwardRef<HTMLTextAreaElement, Props>(
    (
        {
            agentExecution,
            onSuggestionSelect,
            triggerChar = "/",
            popoverSideOffset,
            popoverSide,
            popoverWidthOffset,
            popoverAlignOffset,
            showBuiltInCommands = true,
            disabledMessage,
            className,
            ...textareaProps
        },
        ref,
    ) => {
        const { data: promptsData } = useListCommandPrompts();
        const { membership } = useMembership();
        const isOrgAdmin = membership?.userRole === OrganizationRole.ADMIN;

        // Get support bundle availability if agentExecution is provided
        const { isSupportBundleAvailable } = useAgentExecutionActions(agentExecution);

        // Convert prompts to suggestions and add built-in commands
        const suggestions: CommandSuggestion[] = useMemo(() => {
            const builtInCommands: CommandSuggestion[] = [];

            // Determine which built-in commands to show
            const shouldShowCommand = (commandName: string): boolean => {
                if (showBuiltInCommands === false) return false;
                if (showBuiltInCommands === true) return true;
                return Array.isArray(showBuiltInCommands) && showBuiltInCommands.includes(commandName);
            };

            // Add /clear command
            if (shouldShowCommand("/clear")) {
                builtInCommands.push({
                    value: "/clear",
                    label: "Reset conversation",
                    description: "Clear the chat history and start a new conversation",
                    id: "builtin-clear",
                });
            }

            // Add support bundle command
            if (shouldShowCommand("/support-bundle")) {
                builtInCommands.push({
                    value: "/support-bundle",
                    label: "Download support bundle",
                    description: isSupportBundleAvailable
                        ? "Download a support bundle for debugging"
                        : "Download a support bundle (currently unavailable)",
                    id: "builtin-support-bundle",
                });
            }

            // Only add /commands command if user is org admin
            if (shouldShowCommand("/commands") && isOrgAdmin) {
                builtInCommands.push({
                    value: "/commands",
                    label: "Manage commands",
                    description: "Go to the Agents settings page to manage commands",
                    id: "builtin-commands",
                });
            }

            const promptSuggestions = (promptsData?.prompts || [])
                .filter((prompt: PlainPrompt) => prompt.spec?.command) // Only prompts with commands
                .map((prompt: PlainPrompt) => ({
                    value: `/${prompt.spec?.command}`,
                    label: prompt.metadata?.name || `/${prompt.spec?.command}`,
                    description: prompt.metadata?.description,
                    id: prompt.id,
                }));

            const allSuggestions = [...builtInCommands, ...promptSuggestions];

            // Sort by command length (shortest first), then alphabetically
            return allSuggestions.sort((a, b) => {
                const lengthDiff = a.value.length - b.value.length;
                if (lengthDiff !== 0) {
                    return lengthDiff;
                }
                return a.value.localeCompare(b.value);
            });
        }, [promptsData?.prompts, isOrgAdmin, showBuiltInCommands, isSupportBundleAvailable]);

        return (
            <TextareaWithSuggestions
                ref={ref}
                suggestions={suggestions}
                onSuggestionSelect={onSuggestionSelect}
                triggerChar={triggerChar}
                popoverWidthOffset={popoverWidthOffset}
                popoverSideOffset={popoverSideOffset}
                popoverSide={popoverSide}
                popoverAlignOffset={popoverAlignOffset}
                disabledMessage={disabledMessage}
                className={cn(
                    "w-full max-w-[700px] resize-none rounded-none border-0 bg-transparent p-0 focus-visible:ring-0",
                    className,
                )}
                {...textareaProps}
            />
        );
    },
);

ChatInputWithPrompts.displayName = "ChatInputWithPrompts";

export default ChatInputWithPrompts;
